import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { ChevronDown, Plane, Box } from 'lucide-react';
import { warehouseData } from '../i18n/translations';

const HeroSection = () => {
  const { t } = useLanguage();

  const scrollToProcess = () => {
    const element = document.getElementById('process');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToWarehouses = () => {
    const element = document.getElementById('warehouses');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative flex flex-col hero-bg overflow-hidden"
      style={{ '--hero-bg': "url('/hero.webp')" }}
    >
      {/* Main hero content */}
      <div className="relative min-h-screen flex items-center justify-center pb-20">
      {/* Scanline overlay */}
      <div className="absolute inset-0 scanline-overlay" />
      
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          animate={{
            x: [0, 100, 0],
            y: [0, -50, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute top-1/4 left-1/4 opacity-10"
        >
          <Plane className="w-32 h-32 text-[#7AC142]" />
        </motion.div>
        <motion.div
          animate={{
            x: [0, -80, 0],
            y: [0, 80, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute bottom-1/4 right-1/4 opacity-10"
        >
          <Box className="w-24 h-24 text-[#7AC142]" />
        </motion.div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Label */}
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="eyebrow mb-6"
          >
            Seller Central
          </motion.span>

          {/* Main title */}
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-extrabold text-white tracking-tighter leading-none mb-4">
            {t('hero.title')}
            <br />
            <span className="text-[#7AC142]">{t('hero.titleAccent')}</span>
          </h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mt-6 text-lg md:text-xl text-white/70 max-w-2xl mx-auto font-light"
          >
            {t('hero.subtitle')}
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <button
              onClick={scrollToProcess}
              className="bg-[#7AC142] text-black px-8 py-4 font-bold uppercase tracking-wider btn-shadow hover:bg-[#69A835] transition-colors"
              data-testid="cta-start-shipping"
            >
              {t('hero.cta')}
            </button>
            <button
              onClick={scrollToWarehouses}
              className="border-2 border-white text-white px-8 py-4 font-bold uppercase tracking-wider hover:bg-white hover:text-black transition-all"
              data-testid="cta-view-warehouses"
            >
              {t('hero.ctaSecondary')}
            </button>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator: anchored to the hero, below the CTAs */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10">
        <button
          onClick={scrollToProcess}
          className="text-white/50 hover:text-white transition-colors animate-bounce"
          data-testid="scroll-indicator"
          aria-label={t('a11y.scrollDown')}
        >
          <ChevronDown className="w-8 h-8" />
        </button>
      </div>
      </div>

      {/* Stats bar */}
      <div className="relative bg-black/90 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <p className="text-3xl font-bold text-[#7AC142] font-mono">{warehouseData.length}</p>
              <p className="text-xs text-white/50 uppercase tracking-wider mt-1">{t('hero.stats.originWarehouses')}</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#7AC142] font-mono">5</p>
              <p className="text-xs text-white/50 uppercase tracking-wider mt-1">{t('hero.stats.continents')}</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#7AC142] font-mono">24/7</p>
              <p className="text-xs text-white/50 uppercase tracking-wider mt-1">{t('hero.stats.operations')}</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#7AC142] font-mono">DG</p>
              <p className="text-xs text-white/50 uppercase tracking-wider mt-1">{t('hero.stats.certified')}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
